import { createSelector } from 'reselect';
import cartUtils from '../utils/cart';

// Pricelist catalog layout ('single' | 'double'), defaulting to 'double' when
// unset (existing pricelists / API responses without the field).
export const catalogLayoutSelector = (state) =>
  state.shoot?.shoot?.pricing_model?.catalog_layout || 'double';

// Color mode is on when any group OR product in the pricelist sets a color.
// Memoized so the full scan only reruns when shootProducts changes.
export const anyColorSetSelector = createSelector(
  (state) => state.shoot.shootProducts,
  (shootProducts) =>
    (shootProducts || []).some(
      (group) => group.color || (group.products || []).some((p) => p.color),
    ),
);

// Lock groups unlocked by the current student's cart items. Memoized so the
// derived array keeps a stable identity until the cart actually changes —
// components consume it via useSelector, whose reference equality check would
// otherwise see a fresh array (and force a re-render) on every dispatch.
export const unlockedGroupsSelector = createSelector(
  (state) => state.v3Order.orders,
  (state) => state.additionalData.studentId,
  (orders, studentId) =>
    cartUtils.unlockedGroups(orders?.[studentId]?.cartItems || []),
);

export const personalizationSetToSameValueSelector = (state) => {
  const {
    isPrepay,
    postpayRequiresSameValuePersonalization,
    prepayRequiresSameValuePersonalization,
  } = state.shoot;

  return (
    (isPrepay && prepayRequiresSameValuePersonalization) ||
    (!isPrepay && postpayRequiresSameValuePersonalization)
  );
};

export const cartItemsWithRequiredFieldsSelector = createSelector(
  (state) => state.v3Order.orders,
  (state) => state.additionalData.selectedStudent,
  (state) => state.shoot.shoot,
  (orders, selectedStudent, shootInfo) => {
    const shootEventId = shootInfo?.event_id || shootInfo?.id;
    const orderKey =
      shootEventId && selectedStudent?.id
        ? `${shootEventId}-${selectedStudent.id}`
        : null;
    const order = orderKey ? orders[orderKey] : null;

    if (!order) return [];

    const result = [];
    order.cartItems.forEach((item) => {
      if (item.requiredFields && item.requiredFields.length > 0) {
        result.push(item);
      }
      (item.children || []).forEach((child) => {
        if (child.requiredFields && child.requiredFields.length > 0) {
          result.push(child);
        }
      });
    });

    return result;
  },
);

// Returns a map of { [fieldId]: value } for all required fields across cart items and their children.
// When multiple items share the same field id, the last non-empty value wins.
export const requiredFieldValuesByIdSelector = createSelector(
  cartItemsWithRequiredFieldsSelector,
  (items) => {
    const result = {};
    items.forEach((item) => {
      (item.requiredFields || []).forEach((field) => {
        if (field.id != null && field.value != null && field.value !== '') {
          result[field.id] = field.value;
        }
      });
    });
    return result;
  },
);
